import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, inject, input, viewChild } from '@angular/core';
import { NovaSonicVoiceService } from '../../../utils/nova-sonic-voice.service';

/**
 * VoiceCapsuleComponent
 *
 * Gemini Live–style liquid capsule rendered on a <canvas>. The pill is mostly
 * white; a monochromatic blue "pool" settles at the bottom — as if liquid under
 * gravity — with a soft, feathered surface that gently ripples in place. Motion
 * stays contained to that bottom portion: the surface edge undulates rather than
 * the whole shape pulsing or stretching.
 *
 * Colour encodes who is talking, crossfading so it never hard-cuts between
 * the two DG palettes:
 *   idle      – mostly white; a thin, faint user-blue line resting at the base.
 *   listening – --primary-700 pool rises + ripples from the user's mic signal.
 *   speaking  – --accent-700 pool rises + ripples from the assistant's audio.
 *
 * Phase-accurate motion via FFT:
 *   The ripple is driven by the *actual* voice signal, not a smoothed proxy.
 *   Each frame we pull the active speaker's AnalyserNode from the voice service:
 *     • getByteTimeDomainData → the waveform shape, mapped along the pool's
 *       surface so speech emphasis visibly travels through the edge in real time
 *       (phase-correct, no smoothing applied to time-domain data).
 *     • getByteFrequencyData → low-to-mid band energy, mapped to ripple speed +
 *       intensity so louder / more emphatic speech animates the surface harder.
 *   Fill height is driven by the RMS amplitude the service already meters.
 *
 * Fallback: when no analyser is available yet (or in the idle state) the surface
 * falls back to amplitude-scaled traveling sine waves — the same code path minus
 * the per-sample waveform term — so it degrades gracefully without ever stalling.
 *
 * Performance: the whole loop runs inside requestAnimationFrame, started via
 * NgZone.runOutsideAngular so it never triggers Angular change detection. All
 * per-frame state lives in plain instance fields (refs), never signals, so no
 * re-render is scheduled while animating.
 */

type CapsuleState = 'idle' | 'listening' | 'speaking';

const TWO_PI = Math.PI * 2;

// Spacing (px) between surface control points. Coarser than per-pixel so the
// quadratic spline drawn through them reads as a smooth, flowing edge rather
// than raw, jittery samples.
const SURFACE_STEP = 6;

// Palette anchors — the pool colour lerps between these based on who is
// talking, matching the "500 @ 0% opacity → 700 @ 100% opacity" wave spec.
// Only the 700 (fully opaque, bottom-of-pool) shade matters here since the
// gradient's top stop is always transparent regardless of hue. Values are
// read from the active theme's CSS custom properties so the capsule follows
// brand theming rather than being hardcoded to a single palette.
const DEFAULT_USER_COLOR: readonly [number, number, number] = [0, 101, 196]; // --primary-700 fallback — user / listening
const DEFAULT_USER_COLOR_LIGHT: readonly [number, number, number] = [46, 154, 255];
const DEFAULT_AGENT_COLOR: readonly [number, number, number] = [9, 120, 140]; // --accent-700 fallback — assistant / speaking
const DEFAULT_AGENT_COLOR_LIGHT: readonly [number, number, number] = [32, 159, 182];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Resolves a CSS custom property (e.g. `--primary-700`) on `element` to an
 * `[r, g, b]` tuple. Falls back to `fallback` when the property is unset or
 * isn't a recognisable hex colour (e.g. in test environments without the
 * theme stylesheet loaded).
 */
const resolveCssColor = (
  element: Element,
  cssVar: string,
  fallback: readonly [number, number, number]
): readonly [number, number, number] => {
  const raw = getComputedStyle(element).getPropertyValue(cssVar).trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(raw);
  if (!hex) return fallback;
  const value = hex[1];
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
};

@Component({
  selector: 'app-voice-capsule',
  standalone: true,
  imports: [],
  // Intentionally a single canvas element; all layout lives in the host styles.
  template: `<canvas #canvas></canvas>`,
  styleUrl: './voice-capsule.component.scss',
})
export class VoiceCapsuleComponent implements AfterViewInit, OnDestroy {
  /**
   * Optional manual override for the wave's resting / idle level inside its container.
   * Useful when a parent lays this out as a fixed taller panel in mobile views.
   */
  readonly baseHeight = input<number>(0.08);

  /**
   * Minimum safe zone from the bottom in pixels. The waveform surface will never dip below this point.
   */
  readonly bottomSafeZone = input<number>(0);

  private readonly svc = inject(NovaSonicVoiceService);
  private readonly ngZone = inject(NgZone);
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  private rafId: number | null = null;
  private startTime = 0;

  private resizeObserver: ResizeObserver | null = null;

  // ── Per-frame animated state (refs, never signals) ─────────────────────────
  /** Smoothed 0–1 amplitude of the active speaker; drives pool height. */
  private smoothedLevel = 0;
  /** Smoothed 0–1 low-to-mid spectral energy; drives ripple speed + intensity. */
  private smoothedEnergy = 0;
  /** 0 = light (listening/idle) → 1 = dark (speaking). Lerps for a crossfade. */
  private colorPhase = 0;
  /** Smoothed pool fill height as a 0–1 fraction of the capsule. */
  private fillLevel = 0.08;

  // Reusable buffers, (re)allocated only when the canvas or fftSize changes.
  private surface: Float32Array | null = null;
  private waveBuf: Uint8Array<ArrayBuffer> | null = null;
  private freqBuf: Uint8Array<ArrayBuffer> | null = null;

  // Resolved from the active theme's CSS custom properties on init (see
  // `resolveCssColor`); default to the DG palette until then.
  private userColor: readonly [number, number, number] = DEFAULT_USER_COLOR;
  private userColorLight: readonly [number, number, number] = DEFAULT_USER_COLOR_LIGHT;
  private agentColor: readonly [number, number, number] = DEFAULT_AGENT_COLOR;
  private agentColorLight: readonly [number, number, number] = DEFAULT_AGENT_COLOR_LIGHT;

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    const canvas = this.canvasRef().nativeElement;
    const parent = canvas.parentElement ?? canvas;

    this.userColor = resolveCssColor(canvas, '--primary-700', DEFAULT_USER_COLOR);
    this.userColorLight = resolveCssColor(canvas, '--primary-500', DEFAULT_USER_COLOR_LIGHT);
    this.agentColor = resolveCssColor(canvas, '--accent-700', DEFAULT_AGENT_COLOR);
    this.agentColorLight = resolveCssColor(canvas, '--accent-500', DEFAULT_AGENT_COLOR_LIGHT);

    // Keep canvas pixel dimensions in sync with CSS layout size.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.fitCanvas());
      this.resizeObserver.observe(parent);
    }
    this.fitCanvas();

    // Run the RAF loop outside Angular so no change detection fires per frame.
    this.ngZone.runOutsideAngular(() => {
      this.startTime = performance.now();
      this.tick();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.resizeObserver?.disconnect();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Canvas helpers
  // ─────────────────────────────────────────────────────────────────────────

  private fitCanvas(): void {
    const canvas = this.canvasRef().nativeElement;
    const parent = canvas.parentElement;
    if (!parent) return;
    canvas.width = parent.clientWidth;
    canvas.height = parent.clientHeight;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // State derivation
  // ─────────────────────────────────────────────────────────────────────────

  private getState(): CapsuleState {
    // Anything short of a live, connected session shows the resting idle pool.
    if (this.svc.status() !== 'connected') return 'idle';
    return this.svc.mode() === 'speaking' ? 'speaking' : 'listening';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Animation loop
  // ─────────────────────────────────────────────────────────────────────────

  private readonly tick = (): void => {
    this.rafId = requestAnimationFrame(this.tick);

    const canvas = this.canvasRef().nativeElement;
    const ctx = canvas.getContext('2d');

    // Guard: JSDOM / zero-size canvas during unit tests → bail out.
    if (!ctx || canvas.width === 0 || canvas.height === 0) return;

    const w = canvas.width;
    const h = canvas.height;
    const t = (performance.now() - this.startTime) / 1000; // elapsed seconds
    const state = this.getState();
    const idle = state === 'idle';

    // ── Pull the active speaker's live signal via FFT ───────────────────────
    // Waveform (time domain) gives phase-accurate surface shape; the low-to-mid
    // band of the spectrum gives an energy scalar for ripple speed/intensity.
    const analyser =
      state === 'listening' ? this.svc.getInputAnalyser() : state === 'speaking' ? this.svc.getOutputAnalyser() : null;

    let lowMid = 0;
    if (analyser) {
      const fftLen = analyser.fftSize;
      const binCount = analyser.frequencyBinCount;
      this.waveBuf = this.ensureBuf(this.waveBuf, fftLen);
      this.freqBuf = this.ensureBuf(this.freqBuf, binCount);
      analyser.getByteTimeDomainData(this.waveBuf);
      analyser.getByteFrequencyData(this.freqBuf);

      // Low-to-mid band ≈ the lower quarter of the spectrum: for both the 16 kHz
      // mic and 24 kHz playback analysers this covers roughly the fundamental +
      // lower formants of speech, where emphasis lives.
      const top = Math.max(4, binCount >> 2);
      let sum = 0;
      for (let i = 1; i < top; i++) sum += this.freqBuf[i];
      lowMid = sum / (top - 1) / 255; // 0–1
    }

    // ── Amplitude → smoothed level (fast attack, slow release) ──────────────
    // Scale raw RMS (typically 0–0.12) into a 0–1 perceptual range.
    const rawLevel =
      state === 'listening'
        ? Math.min(1, this.svc.inputLevel * 9)
        : state === 'speaking'
          ? Math.min(1, this.svc.getOutputLevel() * 9)
          : 0;
    this.smoothedLevel += (rawLevel - this.smoothedLevel) * (rawLevel > this.smoothedLevel ? 0.1 : 0.06);
    this.smoothedEnergy += (lowMid - this.smoothedEnergy) * 0.2;

    // ── Colour crossfade (never a hard cut) ─────────────────────────────────
    const targetPhase = state === 'speaking' ? 1 : 0; // idle & listening share the light tone
    this.colorPhase += (targetPhase - this.colorPhase) * 0.06;

    // ── Pool fill height ────────────────────────────────────────────────────
    const idleHeight = this.baseHeight();
    const listenHeight = Math.max(idleHeight, 0.3);
    const speakHeight = Math.max(idleHeight + 0.04, 0.34);

    const baseLevel = idle ? idleHeight : state === 'listening' ? listenHeight : /* speaking */ speakHeight;
    const targetLevel = baseLevel + this.smoothedLevel * 0.42;
    this.fillLevel += (targetLevel - this.fillLevel) * 0.04;

    // Instead of hard-clipping the surface points which flattens the ripple,
    // we recalculate poolTop here to ensure the midline itself never drops
    // too close to the bottom safe zone. We must leave enough room for `voiceAmp` and `baseAmp`
    // to swing down without clipping.
    let poolTop = h * (1 - this.fillLevel);
    const maxDipAllowed = h - this.bottomSafeZone();
    // Allow approx h*0.08 leeway for the downward swing of the ripples
    const maxPoolTop = maxDipAllowed - h * 0.08;
    if (poolTop > maxPoolTop) {
      poolTop = maxPoolTop;
    }

    // Completely lock the ripple if the user is mic-muted while in the 'listening' state
    const isMuted = state === 'listening' && this.svc.micMuted();

    // ── Ripple parameters (contained to the surface, not the whole shape) ────
    // Slow down the travel speed for a calmer roll
    const rippleSpeed = idle || isMuted ? 0.07 : 0.12 + this.smoothedEnergy * 0.7;

    // Rely exclusively on baseAmp for "big, gentle waves" driven by voice volume
    const baseAmp = idle || isMuted ? h * 0.006 : h * 0.006 + this.smoothedLevel * h * 0.08;
    // Make sure the midline (poolTop) does not go too high, so the peaks of the waves
    // don't get chopped off by the top of the container.
    // Max theoretical displacement upward is baseAmp * 1.55.
    const minPoolTop = baseAmp * 1.6;
    if (poolTop < minPoolTop) {
      poolTop = minPoolTop;
    }
    // ── Build the wavy surface at coarse control points ──────────────────────
    // Sampling every SURFACE_STEP px (rather than per pixel) and easing each
    // point toward its target between frames keeps the ripple smooth and fluid
    // for the eye, while the waveform still drives the surface shape.
    const step = SURFACE_STEP;
    const pts = Math.max(2, Math.ceil(w / step) + 1);
    const surf = this.ensureSurface(pts, poolTop);
    let minY = h;
    for (let i = 0; i < pts; i++) {
      const px = Math.min(w, i * step);
      const nx = px / w;
      // Two traveling sines give a gentle, organic base undulation.
      // We widen the sines (0.85 and 1.3) to create luxurious, fat ocean swells.
      let disp =
        Math.sin(nx * TWO_PI * 0.85 + t * rippleSpeed) * baseAmp +
        Math.sin(nx * TWO_PI * 1.3 - t * rippleSpeed * 1.15) * baseAmp * 0.55;

      // We intentionally do NOT add the raw oscilloscope `wv` here anymore.
      // Doing so injects spiky high-frequency audio data directly into the surface,
      // preventing the smooth, calm appearance. The sines above now do 100% of the work.

      // Ease each control point toward its target so the edge glides between
      // frames instead of snapping — the main "smoother for the eye" win.
      const targetY = poolTop - disp; // higher displacement → surface rises

      const y = surf[i] + (targetY - surf[i]) * 0.18;
      surf[i] = y;
      if (y < minY) minY = y;
    }

    // ── Paint ────────────────────────────────────────────────────────────────
    // Left transparent (no fillRect) rather than painted white — the wrapper
    // (.voice-capsule-container) supplies background-color: var(--neutral-200)
    // so the canvas matches the chat's own background instead of a hardcoded
    // colour baked into the drawing code.
    ctx.clearRect(0, 0, w, h);

    // Pool path: bottom edge + a quadratic spline through the control points so
    // the surface curves smoothly rather than in straight facets.
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(0, surf[0]);
    for (let i = 1; i < pts - 1; i++) {
      const x0 = Math.min(i * step, w);
      const x1 = Math.min((i + 1) * step, w);
      const xc = (x0 + x1) / 2;
      const yc = (surf[i] + surf[i + 1]) / 2;
      ctx.quadraticCurveTo(x0, surf[i], xc, yc);
    }
    ctx.lineTo(w, surf[pts - 1]);
    ctx.lineTo(w, h);
    ctx.closePath();

    const r = Math.round(lerp(this.userColor[0], this.agentColor[0], this.colorPhase));
    const g = Math.round(lerp(this.userColor[1], this.agentColor[1], this.colorPhase));
    const b = Math.round(lerp(this.userColor[2], this.agentColor[2], this.colorPhase));

    const rl = Math.round(lerp(this.userColorLight[0], this.agentColorLight[0], this.colorPhase));
    const gl = Math.round(lerp(this.userColorLight[1], this.agentColorLight[1], this.colorPhase));
    const bl = Math.round(lerp(this.userColorLight[2], this.agentColorLight[2], this.colorPhase));

    // Vertical gradient: 500 (light) at the surface feathering to transparent →
    // 500 mid-fill → 700 (dark) solid at the base. The idle pool is dialled
    // down so it reads as a faint line rather than a filled body.
    const peak = idle ? 0.5 : state === 'listening' ? 0.4 : 1;
    const topAlpha = idle ? 0 : 0.25 * peak;
    const fadeTop = Math.max(0, minY - h * 0.1);
    const grad = ctx.createLinearGradient(0, fadeTop, 0, h);
    grad.addColorStop(0, `rgba(${rl},${gl},${bl},${topAlpha})`);
    grad.addColorStop(0.35, `rgba(${rl},${gl},${bl},${(0.7 * peak).toFixed(3)})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},${peak})`);

    ctx.fillStyle = grad;
    ctx.fill();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Buffer helpers
  // ─────────────────────────────────────────────────────────────────────────

  private ensureSurface(pts: number, initY: number): Float32Array {
    if (!this.surface || this.surface.length !== pts) {
      // Seed at the resting surface so the pool doesn't visibly drain in on the
      // first frames after a (re)allocation.
      this.surface = new Float32Array(pts).fill(initY);
    }
    return this.surface;
  }

  private ensureBuf(cur: Uint8Array<ArrayBuffer> | null, len: number): Uint8Array<ArrayBuffer> {
    return cur && cur.length === len ? cur : new Uint8Array(len);
  }
}
